import { TDiscountPage } from '../constants/discountRules'
import { DURATION_CONTEXT, SubscriptionProduct } from '../subscriptionTypes'
import { getCurrencySymbol, isExactPrice } from '../utils/currency'
import { getCompareAtPrice, resolveDiscountPercentage } from '../utils/discount'
import { formatPrice } from '../utils/formatPrice'
import {
	PmsPaymentProviderEnum,
	PmsProductWithOnePrice,
	PmsStripePublicConfiguration,
} from './pmsTypes'

// Best-effort adapter: maps PMS's product shape onto the existing
// SubscriptionProduct shape, so the render chain (PriceItem etc.) doesn't
// need to change yet. Known gaps:
// - price.name/.stripeId, applePayDisabled/googlePayDisabled: no clean PMS
//   equivalent — defaulted to ''/false.
export const adaptPmsProductToSubscriptionProduct = (
	product: PmsProductWithOnePrice,
	locale: string,
	discountPage?: TDiscountPage,
): SubscriptionProduct => {
	const { price, metadata } = product
	const currency = price.currency
	const chargedAmount =
		price.trialAmount > 0 ? price.trialAmount : price.amount

	// Same major-unit conversion as qr-api's mapPriceToProductVariablesDto.
	const regularPrice = isExactPrice(currency)
		? price.amount
		: price.amount / 100
	const trialPrice = isExactPrice(currency)
		? price.trialAmount
		: price.trialAmount / 100
	const discountPercentage = resolveDiscountPercentage(
		metadata?.discountPercentage,
		price.durationUnits,
		discountPage,
	)
	const compareAtPrice = getCompareAtPrice(
		price.trialDays > 0 ? trialPrice : regularPrice,
		discountPercentage,
	)

	// price.metadata.name is qr-api's Price.name migrated verbatim — a
	// headline "title price" override (e.g. "19" for a $228/yr plan, shown
	// as its monthly-equivalent), already in major units, independent of
	// `amount`/100. Confirmed against real data + qr-api's own
	// products-manager.service.ts (`titlePrice: price.name`), 2026-08-10.
	const titlePriceValue = price.metadata?.name
		? Number(price.metadata.name)
		: chargedAmount / (isExactPrice(currency) ? 1 : 100)

	// Needed to init Stripe.js (PaymentButton.tsx's loadStripe(...)) — only
	// present when this price's provider account is actually Stripe.
	// Confirmed against real PMS data (price.providerAccount.configuration).
	const stripePublicKey =
		price.providerAccount.provider === PmsPaymentProviderEnum.STRIPE
			? (
					price.providerAccount
						.configuration as PmsStripePublicConfiguration
				).publicKey
			: undefined

	// The Stripe account's own country. Google Pay's legacy
	// stripe.paymentRequest({ country }) consumed this until MYQRCODE-10790
	// moved Google Pay onto the Express Checkout Element; kept because it's
	// the account-truthful value should a wallet flow need it again.
	const stripeAccountCountry =
		price.providerAccount.provider === PmsPaymentProviderEnum.STRIPE
			? price.providerAccount.countryCode
			: undefined

	// Used as the PayPal SDK's plan_id (PaypalView.tsx) — only present when
	// this specific price row has a paypal_subscription counterpart. None of
	// our real qrcreate-stage data has this yet (no PayPal provider prices
	// seeded there currently).
	const paypalId = price.paypalSubscriptionPrice?.providerId ?? ''

	const paypalSubscriptionPriceId = price.paypalSubscriptionPrice?.id ?? ''

	return {
		id: product.id,
		name: metadata?.nameTranslationKey ?? '',
		recurrentName: metadata?.recurrentNameTranslationKey ?? '',
		description: metadata?.descriptionTranslationKey ?? '',
		priceTitle: metadata?.priceTitleTranslationKey ?? '',
		sku: product.sku,
		isDefault: metadata?.isDefault ?? false,
		isFavourite: metadata?.isFavourite ?? false,
		campaign: product.campaign,
		price: {
			id: price.id,
			name: '',
			currency,
			amount: price.amount,
			trialAmount: price.trialAmount,
			trialDays: price.trialDays,
			stripeId: '',
			paypalId,
			paypalSubscriptionPriceId,
			productId: product.id,
			duration: price.duration ?? 0,
			stripePublicKey,
			stripeAccountCountry,
		},
		priceNumber: isExactPrice(currency)
			? chargedAmount
			: Math.trunc(chargedAmount / 100),
		priceDecimal: isExactPrice(currency) ? 0 : chargedAmount % 100,
		productVariables: {
			currency,
			currencySymbol: getCurrencySymbol(currency),
			titlePrice: String(titlePriceValue),
			titlePriceFormatted: formatPrice(titlePriceValue, currency, locale),
			trialPrice,
			trialPriceFormatted: formatPrice(trialPrice, currency, locale),
			trialDuration: price.trialDays,
			trialContext: DURATION_CONTEXT.DAYS,
			regularPrice,
			regularPriceFormatted: formatPrice(regularPrice, currency, locale),
			regularDuration: price.duration ?? 0,
			regularContext:
				(price.durationUnits as `${DURATION_CONTEXT}`) ??
				DURATION_CONTEXT.MONTHS,
			compareAtPrice,
			compareAtPriceFormatted: formatPrice(
				compareAtPrice,
				currency,
				locale,
			),
			discountPercentage,
		},
		applePayDisabled: false,
		googlePayDisabled: false,
	}
}
